/*
 OK FBI Investigation Game — Forensic Labs + Case Timer + Session Snapshots
 Designed & Developed by OK
*/
import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { api } from "@/lib/api";
import { useSession, assetUrl } from "@/contexts/SessionContext";
import { sfx } from "@/lib/sounds";
import { Fingerprint, Dna, X, Play, Pause, RotateCcw, Timer, Save, Trash2, Camera, CheckCircle2, XCircle, Pencil, Check } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";

import { useAuth } from "@/pages/AuthPage";

/** ============ Forensic Lab full-screen modal ============ */
export function ForensicLab({ open, onClose, tool, caseId, suspects }) {
  const { session, refresh } = useSession();
  const { user, isOwner } = useAuth();
  const plan = user?.membership_plan || (isOwner ? "OWNER" : "FREE");
  const isVipOrHigher = isOwner || ["VIP", "LEGENDARY", "ROYAL", "STREAMER", "OWNER"].includes(String(plan).toUpperCase());
  const [state, setState] = useState("idle"); // idle | scanning | done
  const [progress, setProgress] = useState(0);
  const [result, setResult] = useState(null);
  const stored = (session?.forensic_results || {})[caseId]?.[tool];
  useEffect(() => { if (stored) { setResult(stored); setState("done"); } else { setState("idle"); setResult(null); setProgress(0); } }, [stored, tool, caseId]);
  if (!open) return null;
  const TITLES = { fingerprint: "مختبر البصمات", dna: "مختبر تحليل DNA", audio: "مختبر تحليل الصوت", documents: "مختبر فحص الوثائق", phone: "مختبر تحليل الهاتف", materials: "مختبر فحص المواد" };
  const ICONS = { fingerprint: Fingerprint, dna: Dna };
  const Icon = ICONS[tool] || Fingerprint;

  const runScan = async () => {
    if (!isVipOrHigher && (tool === "dna" || tool === "audio")) {
      toast.error("تحليل DNA والصوت مخصص لباقة VIP فقط — يرجى ترقية الباقة للوصول إليهما");
      return;
    }
    setState("scanning"); setProgress(0);
    if (tool === "fingerprint") sfx.fingerprint(); else if (tool === "dna") sfx.dnaScan(); else sfx.audioEnhance();
    let p = 0;
    const iv = setInterval(() => { p += 4 + Math.random() * 6; if (p >= 100) { p = 100; clearInterval(iv); doAnalyze(); } setProgress(p); }, 90);
  };
  const doAnalyze = async () => {
    try {
      const { data } = await api.post(`/session/forensic/${caseId}/${tool}/run`, {});
      setResult(data);
      setState("done");
      sfx.notification();
      refresh();
    } catch (err) {
      console.error("Forensic analysis failed:", err);
      setState("idle");
      toast.error(err?.response?.data?.detail || "فشل التحليل الجنائي");
    }
  };
  const hide = async () => {
    try {
      await api.post(`/session/forensic/${caseId}/${tool}/hide`);
      setResult(null);
      setState("idle");
      sfx.evidenceUndo();
      refresh();
    } catch (err) {
      console.error("Forensic hide failed:", err);
      toast.error(err?.response?.data?.detail || "تعذر إخفاء النتيجة");
    }
  };
  const rerun = async () => { setResult(null); setState("idle"); setProgress(0); };
  const matchedSuspect = suspects.find(s => s.id === result?.matched_suspect_id);

  return createPortal(
    <AnimatePresence>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        dir="rtl"
        className="fixed inset-0 z-[999] bg-black/95 backdrop-blur p-4 md:p-8 pt-4 flex flex-col overflow-auto" data-testid={`lab-${tool}`}>
        <div className="flex items-center justify-between mb-4 shrink-0">
          <div className="flex items-center gap-3">
            <Icon size={28} className="text-gold" strokeWidth={1.2} />
            <div>
              <div className="text-[10px] tracking-[0.4em] text-gold font-mono-ar">FORENSIC LAB</div>
              <div className="font-cairo text-2xl text-white">{TITLES[tool] || tool}</div>
            </div>
          </div>
          <button onClick={onClose} data-testid="lab-close" className="w-10 h-10 border border-metal hover:border-gold text-zinc-400 hover:text-gold flex items-center justify-center"><X size={18} /></button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-4 flex-1 min-h-0 overflow-hidden">
          {/* LEFT: main visualization */}
          <div className="panel gold-border p-6 flex flex-col relative overflow-hidden">
            <div className="flex items-center justify-between text-[11px] font-mono-ar text-zinc-400 mb-4">
              <span>SAMPLE ID: <span className="text-gold">{result?.sample_id || `${tool.toUpperCase().slice(0,3)}-----`}</span></span>
              <span>STATUS: <span className={state === "done" ? "text-emerald-400" : state === "scanning" ? "text-yellow-400" : "text-zinc-500"}>{state === "idle" ? "READY" : state === "scanning" ? "SCANNING" : "COMPLETE"}</span></span>
            </div>
            <div className="flex-1 grid place-items-center relative">
              {tool === "fingerprint" && (
                <div className="relative w-64 h-64">
                  <img src={assetUrl("/generated_images/tool-fingerprint.png")} alt="" className="w-full h-full object-contain opacity-90" />
                  {state === "scanning" && <div className="absolute inset-x-0 top-0 h-1 bg-gold shadow-[0_0_20px_rgba(197,160,89,0.8)] animate-[scan_1.4s_linear_infinite]" style={{ top: `${progress}%` }} />}
                  {state === "done" && result?.matched_suspect_id && <div className="absolute inset-0 border-4 border-emerald-500 animate-pulse" />}
                </div>
              )}
              {tool === "dna" && (
                <div className="relative w-72 h-72">
                  <img src={assetUrl("/generated_images/tool-dna.png")} alt="" className={`w-full h-full object-contain ${state === "scanning" ? "animate-spin" : ""}`} style={{ animationDuration: "3s" }} />
                  {state === "done" && result?.matched_suspect_id && <div className="absolute inset-0 border-4 border-emerald-500 animate-pulse rounded-full" />}
                </div>
              )}
              {(tool !== "fingerprint" && tool !== "dna") && (
                <div className="text-center">
                  <Icon size={140} strokeWidth={0.8} className="text-gold mx-auto opacity-60" />
                </div>
              )}
            </div>
            {/* Progress bar */}
            {state === "scanning" && (
              <div>
                <div className="text-center text-4xl font-cairo text-gold font-black mb-2 font-mono-ar tabular-nums">{Math.floor(progress)}%</div>
                <div className="h-1 bg-zinc-900 overflow-hidden"><div className="h-full bg-gradient-to-l from-yellow-500 to-yellow-700 transition-all duration-100" style={{ width: `${progress}%` }} /></div>
              </div>
            )}
            {/* Result banner */}
            {state === "done" && (
              <div className={`mt-4 p-4 border-2 ${result?.matched_suspect_id ? "border-emerald-600 bg-emerald-950/30" : "border-red-800 bg-red-950/20"}`} data-testid="lab-result">
                <div className="flex items-center gap-3 mb-2">
                  {result?.matched_suspect_id ? <CheckCircle2 size={28} className="text-emerald-400" /> : <XCircle size={28} className="text-red-400" />}
                  <div>
                    <div className={`text-lg font-cairo font-black ${result?.matched_suspect_id ? "text-emerald-300" : "text-red-300"}`}>
                      {result?.matched_suspect_id ? "MATCH FOUND — تطابق" : "NO MATCH — لا يوجد تطابق"}
                    </div>
                    {result?.matched_suspect_id && <div className="text-sm text-white">تطابق {result.match_percentage}% مع <span className="text-gold font-cairo">{result.matched_suspect_name}</span></div>}
                  </div>
                </div>
                {result?.analysis_text && <div className="text-xs text-zinc-300 mt-2 leading-relaxed border-t border-white/10 pt-2">{result.analysis_text}</div>}
              </div>
            )}
            {/* Controls */}
            <div className="mt-4 flex gap-2 justify-center">
              {state === "idle" && <button onClick={runScan} className="btn-primary" data-testid="lab-start">بدء الفحص</button>}
              {state === "done" && (
                <>
                  <button onClick={hide} className="btn-ghost text-sm text-yellow-400 border-yellow-800/60" data-testid="lab-hide">إخفاء النتيجة</button>
                  <button onClick={rerun} className="btn-ghost text-sm" data-testid="lab-rerun"><RotateCcw size={12} className="inline" /> إعادة الفحص</button>
                </>
              )}
            </div>
          </div>

          {/* RIGHT: suspect comparison */}
          <div className="panel p-4 border-metal overflow-y-auto">
            <div className="label-caps mb-3">مقارنة مع المشتبه بهم</div>
            <div className="space-y-2">
              {suspects.map(s => {
                const isMatch = state === "done" && result?.matched_suspect_id === s.id;
                const pct = isMatch ? result.match_percentage : (state === "done" ? Math.floor(Math.random() * 35) + 10 : 0);
                return (
                  <div key={s.id} className={`flex items-center gap-3 p-2 border ${isMatch ? "border-emerald-600 bg-emerald-950/20" : "border-metal"} rounded-sm`}>
                    <img src={assetUrl(s.portrait)} alt="" className="w-12 h-12 object-cover rounded-sm" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-white font-cairo truncate">{s.name}</div>
                      <div className="text-[10px] text-zinc-500 font-mono-ar">{s.id}</div>
                    </div>
                    <div className="text-left">
                      <div className={`text-lg font-mono-ar font-black ${isMatch ? "text-emerald-400" : state === "done" ? "text-zinc-600" : "text-zinc-800"}`}>{state === "done" ? `${pct}%` : "—"}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
        <div className="text-center pt-3 text-[9px] text-zinc-600 tracking-[0.3em] font-cairo">OK FBI · BY OK</div>
      </motion.div>
    </AnimatePresence>,
    document.body
  );
}

/** ============ Case Timer widget ============ */
export function CaseTimer({ caseId }) {
  const [state, setState] = useState({ elapsed: 0, running: false });
  useEffect(() => {
    let t;
    const load = async () => {
      if (!caseId) return;
      try {
        const { data } = await api.get(`/session/timer/${caseId}`);
        setState(data);
      } catch { /* 401/403/404 — timer silently unavailable */ }
    };
    load();
    t = setInterval(() => setState(s => s.running ? { ...s, elapsed: s.elapsed + 1 } : s), 1000);
    const sync = setInterval(load, 15000);
    return () => { clearInterval(t); clearInterval(sync); };
  }, [caseId]);
  const call = async (action) => {
    sfx.click();
    if (action === "start" || action === "resume") {
      setState(s => ({ ...s, running: true }));
    } else if (action === "pause") {
      setState(s => ({ ...s, running: false }));
    } else if (action === "reset") {
      setState({ elapsed: 0, running: false });
    }
    if (!caseId) return;
    try {
      const { data } = await api.post(`/session/timer/${caseId}/${action}`);
      if (data && typeof data.elapsed === "number") {
        setState(data);
      }
    } catch { /* timer continues running locally even if network/auth fails */ }
  };
  const fmt = (s) => {
    const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), sec = s % 60;
    return (h > 0 ? `${String(h).padStart(2,"0")}:` : "") + `${String(m).padStart(2,"0")}:${String(sec).padStart(2,"0")}`;
  };
  return (
    <div className="panel border-metal px-3 py-2 flex items-center gap-2" data-testid="case-timer">
      <Timer size={14} className={state.running ? "text-gold animate-pulse" : "text-zinc-500"} />
      <span className="text-[10px] text-zinc-500 font-mono-ar">المؤقّت</span>
      <span className="text-sm font-mono-ar text-white tabular-nums">{fmt(state.elapsed)}</span>
      {!state.running ? (
        <button onClick={() => call(state.elapsed === 0 ? "start" : "resume")} data-testid="timer-start" className="text-gold hover:text-white p-1"><Play size={11} /></button>
      ) : (
        <button onClick={() => call("pause")} data-testid="timer-pause" className="text-gold hover:text-white p-1"><Pause size={11} /></button>
      )}
      <button onClick={() => call("reset")} data-testid="timer-reset" className="text-zinc-600 hover:text-red-400 p-1"><RotateCcw size={11} /></button>
    </div>
  );
}

/** ============ Session Snapshots panel ============ */
const SNAPSHOT_PERMISSION_MESSAGE = "لقطات الجلسة متاحة للستريمر أو المالك فقط";
export function SnapshotsPanel({ canManage = true }) {
  const [snaps, setSnaps] = useState([]);
  const [name, setName] = useState("");
  const [restoring, setRestoring] = useState(null);
  const [editing, setEditing] = useState(null);   // snapshot id being renamed
  const [editValue, setEditValue] = useState("");
  const handleSnapshotError = useCallback((e) => {
    if (e?.response?.status === 403) toast.error(SNAPSHOT_PERMISSION_MESSAGE);
    else toast.error("تعذر تنفيذ إجراء اللقطة حالياً");
  }, []);
  const load = useCallback(async () => {
    if (!canManage) { setSnaps([]); return; }
    try { const { data } = await api.get("/snapshots"); setSnaps(data); }
    catch (e) { handleSnapshotError(e); }
  }, [canManage, handleSnapshotError]);
  useEffect(() => { load(); }, [load]);
  const save = async () => {
    if (!canManage) return toast.error(SNAPSHOT_PERMISSION_MESSAGE);
    const nm = name.trim() || `جلسة ${new Date().toLocaleString("ar-EG")}`;
    try { await api.post("/snapshots", { name: nm }); setName(""); sfx.notification(); load(); }
    catch (e) { handleSnapshotError(e); }
  };
  const restore = async (id) => {
    if (!canManage) return toast.error(SNAPSHOT_PERMISSION_MESSAGE);
    try { await api.post(`/snapshots/${id}/restore`); setRestoring(null); sfx.caseSolved(); window.location.reload(); }
    catch (e) { handleSnapshotError(e); }
  };
  const del = async (id) => {
    if (!canManage) return toast.error(SNAPSHOT_PERMISSION_MESSAGE);
    try { await api.delete(`/snapshots/${id}`); load(); }
    catch (e) { handleSnapshotError(e); }
  };
  const beginRename = (s) => { setEditing(s._id); setEditValue(s.name); };
  const commitRename = async (id) => {
    const nm = editValue.trim();
    if (!nm) { setEditing(null); return; }
    if (!canManage) return toast.error(SNAPSHOT_PERMISSION_MESSAGE);
    try { await api.patch(`/snapshots/${id}`, { name: nm }); sfx.click(); setEditing(null); setEditValue(""); load(); }
    catch (e) { handleSnapshotError(e); }
  };
  return (
    <div className="panel p-5 border-metal" data-testid="snapshots-panel">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="label-caps">لقطات الجلسة</div>
          {!canManage && <div className="text-[10px] text-amber-300/80 font-cairo mt-1">متاحة للستريمر أو المالك فقط</div>}
        </div>
        <div className="text-[9px] text-zinc-600 font-mono-ar">SESSION SNAPSHOTS</div>
      </div>
      <div className="flex gap-2 mb-3">
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="اسم الجلسة..." data-testid="snapshot-name"
          disabled={!canManage} title={!canManage ? SNAPSHOT_PERMISSION_MESSAGE : "اسم الجلسة"}
          className="flex-1 bg-black/50 border border-metal rounded-sm px-2 py-1.5 text-xs text-white outline-none focus:border-gold disabled:opacity-40 disabled:cursor-not-allowed" />
        <button onClick={save} className="btn-primary text-xs py-1.5 px-3 disabled:opacity-40 disabled:cursor-not-allowed" data-testid="snapshot-save" disabled={!canManage} title={!canManage ? SNAPSHOT_PERMISSION_MESSAGE : "حفظ اللقطة"}><Save size={12} className="inline" /> حفظ</button>
      </div>
      <div className="space-y-1 max-h-48 overflow-y-auto">
        {snaps.length === 0 && <div className="text-[11px] text-zinc-600 text-center py-3">لا توجد لقطات محفوظة.</div>}
        {snaps.map(s => (
          <div key={s._id} className="flex items-center justify-between px-2 py-1.5 border border-metal/50 hover:border-gold/50 rounded-sm text-xs group">
            <div className="flex-1 min-w-0">
              {editing === s._id ? (
                <input autoFocus value={editValue} onChange={(e) => setEditValue(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") commitRename(s._id); if (e.key === "Escape") setEditing(null); }}
                  data-testid={`snap-rename-input-${s._id}`}
                  className="w-full bg-black/50 border border-gold px-2 py-0.5 text-xs text-white outline-none" />
              ) : (
                <>
                  <div className="text-white truncate font-cairo">{s.name}</div>
                  <div className="text-[9px] text-zinc-500 font-mono-ar">{s.current_case_id || "—"} · {s.progress_pct}% · {new Date(s.saved_at).toLocaleTimeString("ar-EG", { hour: "2-digit", minute: "2-digit" })}</div>
                </>
              )}
            </div>
            {restoring === s._id ? (
              <div className="flex gap-1">
                <button onClick={() => restore(s._id)} data-testid={`snap-restore-confirm-${s._id}`} className="btn-primary text-[10px] py-0.5 px-2">تأكيد</button>
                <button onClick={() => setRestoring(null)} className="btn-ghost text-[10px] py-0.5 px-2">لا</button>
              </div>
            ) : editing === s._id ? (
              <div className="flex gap-1">
                <button onClick={() => commitRename(s._id)} data-testid={`snap-rename-save-${s._id}`} className="text-gold hover:text-white p-1"><Check size={12} /></button>
                <button onClick={() => setEditing(null)} className="text-zinc-500 hover:text-white p-1"><X size={12} /></button>
              </div>
            ) : (
              <div className="flex gap-1">
                <button onClick={() => beginRename(s)} data-testid={`snap-rename-${s._id}`} title="إعادة تسمية" disabled={!canManage} className="text-zinc-500 hover:text-gold p-1 disabled:opacity-40 disabled:cursor-not-allowed"><Pencil size={11} /></button>
                <button onClick={() => setRestoring(s._id)} data-testid={`snap-restore-${s._id}`} disabled={!canManage} title={!canManage ? SNAPSHOT_PERMISSION_MESSAGE : "استعادة"} className="text-gold hover:text-white text-[10px] px-1.5 disabled:opacity-40 disabled:cursor-not-allowed">استعادة</button>
                <button onClick={() => del(s._id)} data-testid={`snap-delete-${s._id}`} disabled={!canManage} title={!canManage ? SNAPSHOT_PERMISSION_MESSAGE : "حذف"} className="text-zinc-600 hover:text-red-400 text-[10px] px-1 disabled:opacity-40 disabled:cursor-not-allowed"><Trash2 size={11} /></button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
